<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Academic Calendar {{ currentYear }}</h1>
            <p class="text-blue-100">Important dates and events for the academic year</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Back button -->
      <div class="mb-4">
        <router-link to="/timetable" class="inline-flex items-center text-blue-900 hover:text-blue-700">
          <span class="material-symbols-outlined mr-1">arrow_back</span>
          Back to Timetables
        </router-link>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <span class="material-symbols-outlined text-5xl text-red-300 mb-3">error</span>
        <h3 class="text-lg font-medium text-red-800 mb-2">Failed to load calendar</h3>
        <p class="text-red-600">{{ error }}</p>
        <button 
          @click="loadCalendar"
          class="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Current Event Card -->
        <div v-if="currentEvent" class="bg-gradient-to-r from-orange-100 to-orange-50 border-l-4 rounded-lg p-5 mb-4 shadow-sm" :style="{ borderColor: getCategoryColor(currentEvent.category).border }">
          <div class="flex items-start gap-4">
            <span class="material-symbols-outlined text-4xl" :style="{ color: getCategoryColor(currentEvent.category).border }">event_available</span>
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <div class="font-semibold text-gray-900 text-lg">
                  {{ currentEvent.title }}
                </div>
                <span class="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-orange-500 text-white animate-pulse">
                  HAPPENING NOW
                </span>
              </div>
              <div class="text-sm text-gray-700 mb-2">
                {{ formatEventDate(currentEvent) }}
              </div>
              <p v-if="currentEvent.details" class="text-sm text-gray-600 mb-2">
                {{ currentEvent.details }}
              </p>
              <span :class="['inline-block px-3 py-1 rounded-full text-xs font-semibold', getCategoryColor(currentEvent.category).badge]">
                {{ formatCategoryLabel(currentEvent.category) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Next Event Card -->
        <div v-if="nextEvent" class="bg-gradient-to-r from-blue-100 to-blue-50 border-l-4 rounded-lg p-5 mb-6 shadow-sm" :style="{ borderColor: getCategoryColor(nextEvent.category).border }">
          <div class="flex items-start gap-4">
            <span class="material-symbols-outlined text-4xl" :style="{ color: getCategoryColor(nextEvent.category).border }">event_upcoming</span>
            <div class="flex-1">
              <div class="font-semibold text-gray-900 text-lg mb-1">
                Next: {{ nextEvent.title }}
              </div>
              <div class="text-sm text-gray-700 mb-2">
                {{ formatEventDate(nextEvent) }}
              </div>
              <p v-if="nextEvent.details" class="text-sm text-gray-600 mb-2">
                {{ nextEvent.details }}
              </p>
              <div class="flex gap-2">
                <span :class="['inline-block px-3 py-1 rounded-full text-xs font-semibold', getCategoryColor(nextEvent.category).badge]">
                  {{ formatCategoryLabel(nextEvent.category) }}
                </span>
                <span class="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white">
                  {{ getTimeUntilEvent(nextEvent) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Filter Bar -->
        <div class="bg-white rounded-lg shadow mb-6 p-4">
          <div class="flex flex-col md:flex-row gap-4">
            <!-- Search -->
            <div class="flex-1">
              <div class="relative">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  <span class="material-symbols-outlined">search</span>
                </span>
                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Search events..."
                  class="pl-10 pr-4 py-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <!-- Category Filter -->
            <div class="flex gap-2 flex-wrap">
              <button
                @click="selectedCategory = 'all'"
                :class="[
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  selectedCategory === 'all' 
                    ? 'bg-blue-900 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                ]"
              >
                All ({{ events.length }})
              </button>
              <button
                v-for="cat in categories"
                :key="cat.value"
                @click="selectedCategory = cat.value"
                :class="[
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                  selectedCategory === cat.value 
                    ? getCategoryColor(cat.value).buttonActive
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                ]"
              >
                {{ cat.label }} ({{ getEventCountByCategory(cat.value) }})
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredEvents.length === 0" class="bg-white rounded-lg shadow p-8 text-center">
          <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">event_busy</span>
          <h3 class="text-lg font-medium text-gray-700 mb-2">No events found</h3>
          <p class="text-gray-500">Try adjusting your search or filter criteria.</p>
        </div>

        <!-- Events List -->
        <div v-else class="space-y-4">
          <!-- Grouped by Month -->
          <div 
            v-for="(monthEvents, month) in groupedByMonth" 
            :key="month"
            class="bg-white rounded-lg shadow overflow-hidden"
          >
            <div class="bg-blue-900 text-white px-6 py-3">
              <h3 class="font-semibold text-lg">{{ month }}</h3>
            </div>
            
            <div class="divide-y divide-gray-200">
              <div 
                v-for="event in monthEvents" 
                :key="event.title + event.date"
                class="p-4 hover:bg-gray-50 transition-colors border-l-4"
                :style="{ borderLeftColor: getCategoryColor(event.category, event.title).border }"
                :class="{ 'bg-orange-50': isEventToday(event) }"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 class="font-semibold text-gray-900">{{ event.title }}</h4>
                      <span :class="['inline-block px-2 py-0.5 rounded-full text-xs font-medium', getCategoryColor(event.category, event.title).badge]">
                        {{ formatCategoryLabel(event.category) }}
                      </span>
                      <span v-if="isEventToday(event)" class="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-500 text-white animate-pulse">
                        TODAY
                      </span>
                    </div>
                    
                    <div class="text-sm text-gray-600 mb-1">
                      <span class="material-symbols-outlined text-sm align-middle mr-1">calendar_today</span>
                      {{ formatEventDate(event) }}
                    </div>
                    
                    <p v-if="event.details" class="text-sm text-gray-500">
                      {{ event.details }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import { getAcademicCalendar, getUpcomingEvents, getEventsForDate, formatEventDate } from '@/composables/useAcademicCalendar';

export default {
  name: 'AcademicCalendarPage',
  components: {
    AppHeader,
    BottomNav
  },
  data() {
    return {
      events: [],
      loading: true,
      error: null,
      searchQuery: '',
      selectedCategory: 'all',
      currentYear: new Date().getFullYear(),
      categories: [
        { label: 'Public Holidays', value: 'public_holiday' },
        { label: 'Academic', value: 'academic' },
        { label: 'Exams', value: 'exam' },
        { label: 'Tests', value: 'test_week' },
        { label: 'Registration', value: 'registration' },
        { label: 'Recess', value: 'recess' }
      ]
    };
  },
  computed: {
    filteredEvents() {
      let filtered = this.events;
      
      // Filter by category
      if (this.selectedCategory !== 'all') {
        filtered = filtered.filter(event => event.category === this.selectedCategory);
      }
      
      // Filter by search query
      if (this.searchQuery) {
        const search = this.searchQuery.toLowerCase();
        filtered = filtered.filter(event => 
          event.title.toLowerCase().includes(search) ||
          (event.details && event.details.toLowerCase().includes(search))
        );
      }
      
      // Sort by date
      return filtered.sort((a, b) => {
        const dateA = new Date(a.isRange ? a.startDate : a.date);
        const dateB = new Date(b.isRange ? b.startDate : b.date);
        return dateA - dateB;
      });
    },
    
    groupedByMonth() {
      const grouped = {};
      
      this.filteredEvents.forEach(event => {
        const date = new Date(event.isRange ? event.startDate : event.date);
        const monthKey = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push(event);
      });
      
      return grouped;
    },
    
    currentEvent() {
      // Get events happening today
      const today = new Date();
      const currentEvents = getEventsForDate(this.events, today);
      return currentEvents.length > 0 ? currentEvents[0] : null;
    },
    
    nextEvent() {
      const upcoming = getUpcomingEvents(this.events, 365);
      // Filter out current events
      const futureEvents = upcoming.filter(event => {
        if (this.currentEvent && event.title === this.currentEvent.title) {
          return false;
        }
        return true;
      });
      return futureEvents.length > 0 ? futureEvents[0] : null;
    }
  },
  async mounted() {
    await this.loadCalendar();
  },
  methods: {
    async loadCalendar() {
      this.loading = true;
      this.error = null;
      
      try {
        const result = await getAcademicCalendar();
        
        if (result.success) {
          this.events = result.events;
          this.currentYear = result.year;
          console.log(`Loaded ${this.events.length} calendar events for ${this.currentYear}`);
        } else {
          this.error = result.message || 'Failed to load academic calendar';
        }
      } catch (err) {
        console.error('Error loading calendar:', err);
        this.error = err.message || 'An unexpected error occurred';
      } finally {
        this.loading = false;
      }
    },
    
    formatEventDate(event) {
      return formatEventDate(event);
    },
    
    formatCategoryLabel(category) {
      return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    getCategoryColor(category, eventTitle = '') {
      // UP official color codes mapping
      const specialEvents = {
        "Welcome Day": { border: "#B1A0C7", badge: "bg-[#B1A0C7] text-white", buttonActive: "bg-[#B1A0C7] text-white" },
        "INSYNC First-Year Concert": { border: "#F4CDA8", badge: "bg-[#F4CDA8] text-gray-800", buttonActive: "bg-[#F4CDA8] text-gray-800" },
        "RAG of Hope Day": { border: "#FF66CC", badge: "bg-[#FF66CC] text-white", buttonActive: "bg-[#FF66CC] text-white" },
        "Choose UP Day": { border: "#D9A694", badge: "bg-[#D9A694] text-white", buttonActive: "bg-[#D9A694] text-white" },
        "UP Wellness Day": { border: "#948A54", badge: "bg-[#948A54] text-white", buttonActive: "bg-[#948A54] text-white" },
        "Senate Review Committee on Re-admissions": { border: "#00B0F0", badge: "bg-[#00B0F0] text-white", buttonActive: "bg-[#00B0F0] text-white" },
        "Summer School": { border: "#FFFF00", badge: "bg-[#FFFF00] text-gray-900", buttonActive: "bg-[#FFFF00] text-gray-900" },
        "Winter School": { border: "#FFFF00", badge: "bg-[#FFFF00] text-gray-900", buttonActive: "bg-[#FFFF00] text-gray-900" },
        "Friday Timetable": { border: "#FF0000", badge: "bg-[#FF0000] text-white", buttonActive: "bg-[#FF0000] text-white" },
        "Monday Timetable": { border: "#FF0000", badge: "bg-[#FF0000] text-white", buttonActive: "bg-[#FF0000] text-white" },
        "Wednesday Timetable": { border: "#FF0000", badge: "bg-[#FF0000] text-white", buttonActive: "bg-[#FF0000] text-white" }
      };

      // Special event color override
      if (eventTitle && specialEvents[eventTitle]) {
        return specialEvents[eventTitle];
      }

      // Category color mapping
      const colors = {
        public_holiday: { border: "#FFCC00", badge: "bg-[#FFCC00] text-gray-900", buttonActive: "bg-[#FFCC00] text-gray-900" },
        registration: { border: "#00B0F0", badge: "bg-[#00B0F0] text-white", buttonActive: "bg-[#00B0F0] text-white" },
        orientation: { border: "#00B0F0", badge: "bg-[#00B0F0] text-white", buttonActive: "bg-[#00B0F0] text-white" },
        event: { border: "#F4CDA8", badge: "bg-[#F4CDA8] text-gray-800", buttonActive: "bg-[#F4CDA8] text-gray-800" },
        test_week: { border: "#92D050", badge: "bg-[#92D050] text-gray-900", buttonActive: "bg-[#92D050] text-gray-900" },
        exam: { border: "#9DC3E6", badge: "bg-[#9DC3E6] text-gray-900", buttonActive: "bg-[#9DC3E6] text-gray-900" },
        supplementary_exam: { border: "#60497A", badge: "bg-[#60497A] text-white", buttonActive: "bg-[#60497A] text-white" },
        recess: { border: "#FFFF00", badge: "bg-[#FFFF00] text-gray-900", buttonActive: "bg-[#FFFF00] text-gray-900" },
        academic: { border: "#003B5C", badge: "bg-[#003B5C] text-white", buttonActive: "bg-[#003B5C] text-white" },
        graduation: { border: "#B1A0C7", badge: "bg-[#B1A0C7] text-white", buttonActive: "bg-[#B1A0C7] text-white" }
      };
      return colors[category] || { border: "#6B7280", badge: "bg-gray-100 text-gray-800", buttonActive: "bg-gray-600 text-white" };
    },
    
    getEventCountByCategory(category) {
      return this.events.filter(event => event.category === category).length;
    },
    
    isEventToday(event) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (event.isRange) {
        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        return today >= startDate && today <= endDate;
      } else {
        const eventDate = new Date(event.date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate.getTime() === today.getTime();
      }
    },
    
    getTimeUntilEvent(event) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const eventDate = new Date(event.isRange ? event.startDate : event.date);
      eventDate.setHours(0, 0, 0, 0);
      
      const diffTime = eventDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Tomorrow';
      if (diffDays < 7) return `In ${diffDays} days`;
      if (diffDays < 30) return `In ${Math.floor(diffDays / 7)} weeks`;
      if (diffDays < 365) return `In ${Math.floor(diffDays / 30)} months`;
      return 'This year';
    }
  }
};
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>
